import { CurrentPlanDetails, PlanInterval } from "@/store/checkout/types";
import {
  TenantMembership,
  PlanType,
  TenantType,
  TenantTypeEnum,
  TenantDetailedType,
} from "@/store/tenants/endpoints/tenants";

export type NonEmptyArray<T> = [T, ...T[]];

export const decideMultiTenancy = (
  tenantsList: NonEmptyArray<TenantType>
): {
  shouldSelectTenant: boolean | undefined;
  selectedTenant: TenantType | NonEmptyArray<TenantType> | undefined;
} => {
  if (!tenantsList || tenantsList.length === 0) {
    console.warn("Tenants list is empty or undefined.");
    return { shouldSelectTenant: undefined, selectedTenant: undefined };
  }

  const isBasic = (tenant: TenantType) => tenant.tenantType === TenantTypeEnum.Personal;
  const isBusinessOrEnterprise = (tenant: TenantType) =>
    tenant.tenantType === TenantTypeEnum.Commercial;

  const ownedBasic = tenantsList.filter(
    tenant => isBasic(tenant) && tenant.membership === TenantMembership.OWNER
  );

  const ownedBusinessOrEnterprise = tenantsList.filter(
    tenant => isBusinessOrEnterprise(tenant) && tenant.membership === TenantMembership.OWNER
  );

  const memberedBusinessOrEnterprise = tenantsList.filter(
    tenant => isBusinessOrEnterprise(tenant) && tenant.membership === TenantMembership.MEMBER
  );

  const userOwnsBusinessOrEnterprise = ownedBusinessOrEnterprise.length > 0;
  const userIsPartOfBusinessOrEnterprise = memberedBusinessOrEnterprise.length > 0;

  // Multiple business or enterprise tenants and owner of Basic
  if (
    (userOwnsBusinessOrEnterprise || userIsPartOfBusinessOrEnterprise) &&
    [...memberedBusinessOrEnterprise, ...ownedBusinessOrEnterprise].length > 1
  ) {
    return {
      shouldSelectTenant: true,
      selectedTenant: [
        ...memberedBusinessOrEnterprise,
        ...ownedBusinessOrEnterprise,
        ...ownedBasic,
      ] as NonEmptyArray<TenantType>,
    };
  }

  // Member of one business or enterprise, but not an owner of business or enterprise
  if (
    userIsPartOfBusinessOrEnterprise &&
    memberedBusinessOrEnterprise.length === 1 &&
    !userOwnsBusinessOrEnterprise
  ) {
    return {
      shouldSelectTenant: false,
      selectedTenant: [memberedBusinessOrEnterprise[0], ...ownedBasic] as NonEmptyArray<TenantType>,
    };
  }

  // Owns one business or enterprise plan + basic owner
  if (userOwnsBusinessOrEnterprise && ownedBusinessOrEnterprise.length === 1) {
    return {
      shouldSelectTenant: false,
      selectedTenant: [ownedBusinessOrEnterprise[0], ...ownedBasic] as NonEmptyArray<TenantType>,
    };
  }

  // Single basic tenant
  if (tenantsList.length === 1 && tenantsList[0].tenantType === TenantTypeEnum.Personal) {
    return {
      shouldSelectTenant: false,
      selectedTenant: tenantsList[0],
    };
  }

  console.warn("No matching case found for tenants list.");
  return { shouldSelectTenant: undefined, selectedTenant: undefined };
};

export const getTenantInfo = (
  tenant: TenantDetailedType & {
    plan: CurrentPlanDetails & { recurringInterval: PlanInterval | null };
    isCrmVaultXMember?: boolean;
  }
) => {
  return {
    isBasic: tenant.type === TenantTypeEnum.Personal,
    isOwner: tenant.membership === TenantMembership.OWNER,
    isBusiness: tenant.licensesInfo.some(lic => lic.license === PlanType.BUSINESS),
    isEnterprise: tenant.licensesInfo.some(lic => lic.license === PlanType.ENTERPRISE),
    isCRM: tenant.licensesInfo.some(lic => lic.license === PlanType.ICRM),
    isCrmVaultXMember: tenant.isCrmVaultXMember ?? false,
    interval: tenant.plan.recurringInterval,
    plan: tenant.plan,
    tenant,
  };
};
